import ErrorBoundary from '@/components/ErrorBoundary/ErrorBoundary';
import LibraryItemCard from '@/components/Library/LibraryItemCard';
import { LibraryItemHoverButton } from '@/components/Library/LibraryItemHoverButton';
import LibraryItemHoverOverlay from '@/components/Library/LibraryItemHoverOverlay';
import LibraryItemImage from '@/components/Library/LibraryItemImage';
import { MaskedIcon } from '@/components/MaskedIcon/MaskedIcon';
import Graph from '@/images/Graph.svg';
import { CAMPAIGN_CONTAINER_MAX_WIDTH } from '@/pages/campaign/constants';
import { VideoBadge } from '@/pages/home/components/Common';
import { useAnalytics } from '@/services/analytics';
import { CampaignStatus, ICampaign } from '@d-id/types';
import styled from '@emotion/styled';
import { ReactElement, useState } from 'react';
import { AnalyticsModal } from '../Modals';
import { OptionsList } from './OptionsList';

const Container = styled(LibraryItemCard)`
    width: 100%;
    max-width: ${CAMPAIGN_CONTAINER_MAX_WIDTH}px;
    margin-top: 0;
`;

interface CampaignTileProps {
    campaign: ICampaign;
    openAssetsModal: () => void;
    openCreditsCalculatorModal: () => void;
}

const CampaignsTile = ({ campaign, openAssetsModal, openCreditsCalculatorModal }: CampaignTileProps) => {
    const isError = campaign.status === CampaignStatus.Error;
    const [keepHover, setKeepHover] = useState(false);
    const [showAnalytics, setShowAnalytics] = useState(false);
    const mixpanel = useAnalytics();

    return (
        <Container item={campaign}>
            <LibraryItemImage errorText="Failed to generate campaign" src={campaign.presenter.thumbnail} isError={isError} size="cover">
                <LibraryItemHoverOverlay
                    show={keepHover}
                    className="video-container-hover"
                    dropdown={
                        <OptionsList
                            campaign={campaign}
                            onOpen={() => setKeepHover(true)}
                            onClose={() => setKeepHover(false)}
                            openAnalyticsModal={() => setShowAnalytics(true)}
                            openAssetsModal={openAssetsModal}
                            openCreditsCalculatorModal={openCreditsCalculatorModal}
                        />
                    }>
                    {!isError && (
                        <LibraryItemHoverButton
                            color="secondary"
                            onClick={() => {
                                mixpanel?.track('campaigns-analytics', { campaign, event: 'click', from: 'card-hover' });
                                setShowAnalytics(true);
                            }}
                            icon={<MaskedIcon src={Graph} size="16px" />}
                            text="View analytics"
                            id="campaign-analytics-btn"
                        />
                    )}
                </LibraryItemHoverOverlay>
                <VideoBadge duration={campaign.duration} />
            </LibraryItemImage>

            <AnalyticsModal campaign={campaign} isOpen={showAnalytics} handleClose={() => setShowAnalytics(false)} />
        </Container>
    );
};

const CampaignTileErrorBoundary = (props: CampaignTileProps): ReactElement => (
    <ErrorBoundary
        fallback={<CampaignsTile {...props} />}
        beforeCapture={scope => scope.setTags({ component: 'CampaignTile', id: props.campaign.id, status: props.campaign.status })}>
        <CampaignsTile {...props} />
    </ErrorBoundary>
);

export { CampaignTileErrorBoundary as CampaignTile };
